class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.refreshGeneration = 0;
    this.refreshRequested = false;
    this.refreshPromise = null;
    this.quantityChangePending = false;
    this.mutationPending = false;
    this.pendingExternalMutations = 0;
    this.unverified = false;
    this.pendingOpen = false;
    this.announceUpdate = false;
    this.disabledControls = new Map();

    this.onHeaderClick = (event) => {
      const link = event.target.closest?.('#cart-icon-bubble');
      if (this.isCartPage() || !link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(link);
    };
    this.onHeaderKeydown = (event) => {
      if (!this.isCartPage() && event.code === 'Space' && event.target.closest?.('#cart-icon-bubble')) {
        event.preventDefault();
        this.open(event.target.closest('#cart-icon-bubble'));
      }
    };
    this.onBundleAdded = () => this.refresh({ open: true, announce: true });
    this.onPageShow = (event) => {
      if (event.persisted) this.refresh();
    };
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape' && this.classList.contains('active')) this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest?.('#CartDrawer-Overlay')) this.close();
      if (event.target.closest?.('[data-gjl-drawer-retry]')) {
        event.preventDefault();
        this.refresh({ announce: true });
      }
      // Block both native and app checkout handlers while the cart is unsettled.
      if (event.target.closest?.('#CartDrawer-Checkout') && !this.canCheckout()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    this.addEventListener('submit', (event) => {
      if (event.target.id === 'CartDrawer-Form' && !this.canCheckout()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    document.addEventListener('click', this.onHeaderClick);
    document.addEventListener('keydown', this.onHeaderKeydown);
    document.addEventListener('bndlr:bundle_added_to_cart', this.onBundleAdded);
    window.addEventListener('pageshow', this.onPageShow);
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // product-form calls renderContents itself; our own writes already refresh.
      if (event.source === 'product-form' || event.source === 'gjl-cart-drawer') return;
      return this.refresh();
    });
    this.syncCheckoutState();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onHeaderClick);
    document.removeEventListener('keydown', this.onHeaderKeydown);
    document.removeEventListener('bndlr:bundle_added_to_cart', this.onBundleAdded);
    window.removeEventListener('pageshow', this.onPageShow);
    this.cartUpdateUnsubscriber?.();
    this.refreshGeneration += 1;
    this.refreshRequested = false;
    this.pendingOpen = false;
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const link = document.querySelector('#cart-icon-bubble');
    if (!link) return;
    if (this.isCartPage()) {
      for (const attribute of ['role', 'aria-haspopup', 'aria-controls', 'aria-expanded']) link.removeAttribute(attribute);
      return;
    }
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', 'CartDrawer');
    link.setAttribute('aria-expanded', String(this.classList.contains('active')));
  }

  isCartPage() {
    if (document.querySelector('cart-items')) return true;
    const cartPath = new URL(routes.cart_url, window.location.origin).pathname.replace(/\/$/, '');
    return window.location.pathname.replace(/\/$/, '') === cartPath;
  }

  hasPendingCartPageUpdate() {
    const cart = document.querySelector('cart-items');
    return Boolean(cart && (cart.quantityChangePending || cart.pendingQuantityUpdates > 0 || cart.cartRefreshPending));
  }

  beginExternalMutation() {
    this.pendingExternalMutations += 1;
    this.refreshRequested = true;
    this.refreshGeneration += 1;
    this.syncCheckoutState();
  }

  endExternalMutation() {
    if (this.pendingExternalMutations === 0) return Promise.resolve(false);
    this.pendingExternalMutations -= 1;
    const cartPage = document.querySelector('cart-items');
    if (this.pendingExternalMutations > 0) {
      this.syncCheckoutState();
      return Promise.resolve(false);
    }
    // Even a lost add response may have committed. Re-read both visible cart
    // surfaces after the final add settles; never replay the write.
    cartPage?.onCartUpdate();
    const refresh = this.refresh();
    return refresh;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    else if (!this.classList.contains('active')) this.setActiveElement(document.activeElement);
    const refresh = this.refresh();
    this.show();
    return refresh;
  }

  show() {
    if (!this.isConnected) return;
    if (!this.classList.contains('active') && !this.activeElement) this.setActiveElement(document.activeElement);
    if (!document.body.classList.contains('overflow-hidden')) {
      this.ownsScrollLock = true;
      document.body.classList.add('overflow-hidden');
    }
    this.classList.add('animate', 'active');
    this.setHeaderCartIconAccessibility();
    const dialog = this.querySelector('.drawer__inner');
    if (dialog) trapFocus(dialog, dialog);
  }

  close() {
    this.pendingOpen = false;
    const wasOpen = this.classList.contains('active');
    this.classList.remove('active');
    if (wasOpen) removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : undefined);
    if (this.ownsScrollLock) document.body.classList.remove('overflow-hidden');
    this.ownsScrollLock = false;
    this.setHeaderCartIconAccessibility();
  }

  // Public Dawn interface. Add responses can describe only the added line(s),
  // so read the complete server-rendered cart instead of reusing their totals.
  renderContents(parsedState) {
    this.productId = parsedState?.id;
    return this.refresh({ open: true, announce: true });
  }

  refresh({ open = false, announce = false } = {}) {
    if (open) {
      if (!this.classList.contains('active') && !this.activeElement) this.setActiveElement(document.activeElement);
      this.pendingOpen = true;
    }
    this.announceUpdate ||= announce;
    this.refreshRequested = true;
    this.refreshGeneration += 1;
    this.syncCheckoutState();
    return this.drainRefresh();
  }

  drainRefresh() {
    if (this.refreshPromise) return this.refreshPromise;
    if (this.quantityChangePending || this.mutationPending || this.pendingExternalMutations > 0 || !this.isConnected) return Promise.resolve(false);
    this.refreshPromise = this.runRefresh().finally(() => {
      this.refreshPromise = null;
      if (this.refreshRequested && !this.quantityChangePending && !this.mutationPending && this.pendingExternalMutations === 0 && this.isConnected) {
        return this.drainRefresh();
      }
      this.syncCheckoutState();
    });
    return this.refreshPromise;
  }

  async runRefresh() {
    let updated = false;
    while (this.refreshRequested && !this.quantityChangePending && !this.mutationPending && this.pendingExternalMutations === 0 && this.isConnected) {
      this.refreshRequested = false;
      const generation = this.refreshGeneration;
      const url = new URL(routes.cart_url, window.location.origin);
      url.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
      try {
        const response = await fetch(url.toString(), { cache: 'no-store' });
        if (!response.ok) throw new Error(this.errorText());
        const sections = await response.json();
        if (generation !== this.refreshGeneration || !this.isConnected) continue;
        this.applySections(sections);
        this.unverified = false;
        this.setError('');
        updated = true;
        if (this.pendingOpen) {
          this.pendingOpen = false;
          this.show();
        }
      } catch (error) {
        if (generation !== this.refreshGeneration || !this.isConnected) continue;
        this.unverified = true;
        this.setError(this.errorText());
        // Keep the explicit failure reachable after a successful add. Checkout
        // remains locked until the user retries or opens the full cart page.
        if (this.pendingOpen) {
          this.pendingOpen = false;
          this.show();
        }
        console.error(error);
      }
    }
    return updated;
  }

  applySections(sections) {
    const drawer = this.getSectionDOM(sections?.['cart-drawer'], '#CartDrawer');
    const bubble = this.getSectionDOM(sections?.['cart-icon-bubble']);
    const count = Number(drawer?.dataset.cartCount);
    const total = Number(drawer?.dataset.cartTotal);
    const discount = Number(drawer?.dataset.cartDiscount);
    if (!drawer || !bubble || !Number.isInteger(count) || count < 0 || !Number.isFinite(total) || total < 0 ||
        !Number.isFinite(discount) || discount < 0 || !/^[A-Z]{3}$/.test(drawer.dataset.currency || '') ||
        !drawer.querySelector('.drawer__inner')) throw new Error(this.errorText());
    const target = this.querySelector('#CartDrawer');
    if (!target) throw new Error(this.errorText());
    const focused = this.contains(document.activeElement) ? document.activeElement.id : null;
    this.restoreControls();
    target.innerHTML = drawer.innerHTML;
    for (const key of ['cartCount', 'cartTotal', 'cartDiscount', 'currency']) target.dataset[key] = drawer.dataset[key];
    const cartLink = document.getElementById('cart-icon-bubble');
    if (cartLink) cartLink.innerHTML = bubble.innerHTML;
    this.classList.toggle('is-empty', count === 0);
    this.setHeaderCartIconAccessibility();
    if (this.classList.contains('active')) {
      const dialog = this.querySelector('.drawer__inner');
      const focus = (focused && document.getElementById(focused)) || dialog;
      trapFocus(dialog, focus);
    }
  }

  beginQuantityChange(event) {
    if (!event.target.matches?.('input[name="updates[]"][data-index]')) return false;
    if (this.mutationPending || this.quantityChangePending || this.pendingExternalMutations > 0 || this.unverified) return false;
    this.quantityChangePending = true;
    this.refreshRequested = true;
    this.refreshGeneration += 1;
    this.setError('');
    this.syncCheckoutState();
    return true;
  }

  validateQuantity(event) {
    const input = event.target;
    if (!this.contains(input)) {
      this.quantityChangePending = false;
      return this.refresh();
    }
    const value = Number(input.value);
    const min = Number(input.dataset.min || input.min || 0);
    const max = input.max === '' ? Infinity : Number(input.max);
    const step = Number(input.step || 1);
    let message = '';
    if (!Number.isInteger(value) || value < min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', min);
    } else if (value > max) {
      message = window.quickOrderListStrings.max_error.replace('[max]', max);
    } else if (value % step !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', step);
    }
    this.quantityChangePending = false;
    if (message) {
      input.value = input.getAttribute('value');
      this.setError(message);
      this.syncCheckoutState();
      // A change invalidated any previous read. Re-read before unlocking.
      return this.refresh().then(() => this.setError(message));
    }
    return this.changeQuantity(input.dataset.index, value, event, input.name, input.dataset.quantityVariantId);
  }

  async changeQuantity(line, quantity, event, name, variantId) {
    if (this.mutationPending || this.quantityChangePending || this.pendingExternalMutations > 0 || this.unverified) return false;
    const input = this.querySelector(`#Drawer-quantity-${Number(line)}`);
    if (!input || !Number.isInteger(Number(quantity)) || Number(quantity) < 0) return false;
    const identity = input.dataset.lineKey ? { id: input.dataset.lineKey } : { line: Number(line) };
    this.mutationPending = true;
    this.refreshRequested = true;
    this.refreshGeneration += 1;
    this.setError('');
    this.syncCheckoutState();
    let state;
    let errorMessage = '';
    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ ...identity, quantity: Number(quantity) }),
      });
      state = await response.json();
      if (!response.ok || state.errors || state.status) {
        errorMessage = typeof state.description === 'string' ? state.description :
          typeof state.errors === 'string' ? state.errors : this.errorText();
      }
    } catch (error) {
      // The server may have accepted the write. Recover by reading; never
      // repeat the mutation, which could otherwise change the wrong quantity.
      errorMessage = this.errorText();
      console.error(error);
    } finally {
      this.mutationPending = false;
    }
    // The response may be lost after the server accepted this mutation. Keep
    // the full cart's line indexes locked until its own fresh read completes.
    document.querySelector('cart-items')?.onCartUpdate();
    const updated = await this.refresh({ announce: !errorMessage });
    if (errorMessage) this.setError(errorMessage);
    if (updated && state && !errorMessage) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gjl-cart-drawer', cartData: state, variantId });
    }
    return updated && !errorMessage;
  }

  isBusy() {
    return this.quantityChangePending || this.mutationPending || this.pendingExternalMutations > 0 || this.refreshRequested || Boolean(this.refreshPromise);
  }

  canCheckout() {
    return !this.isBusy() && !this.hasPendingCartPageUpdate() && !this.unverified && Number(this.querySelector('#CartDrawer')?.dataset.cartCount) > 0;
  }

  syncCheckoutState() {
    const busy = this.isBusy() || this.hasPendingCartPageUpdate();
    const locked = busy || this.unverified;
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (locked) {
      this.querySelectorAll('input[name="updates[]"], quantity-input button, cart-remove-button button').forEach((control) => {
        if (!this.disabledControls.has(control)) this.disabledControls.set(control, control.disabled);
        control.disabled = true;
      });
    } else this.restoreControls();
    if (checkout) {
      checkout.disabled = !this.canCheckout();
      if (busy) checkout.setAttribute('aria-busy', 'true');
      else checkout.removeAttribute('aria-busy');
    }
    const dialog = this.querySelector('.drawer__inner');
    if (dialog) {
      dialog.setAttribute('aria-busy', String(busy));
      dialog.dataset.cartState = busy ? 'updating' : this.unverified ? 'error' : 'ready';
    }
    const panel = this.querySelector('#CartDrawer');
    const status = this.querySelector('[data-gjl-drawer-status]');
    if (status) status.textContent = busy ? panel?.dataset.updatingText || '' :
      this.announceUpdate && !this.unverified ? panel?.dataset.updatedText || '' : '';
    if (!busy) this.announceUpdate = false;
    const retry = this.querySelector('[data-gjl-drawer-retry]');
    if (retry) retry.disabled = busy;
    document.querySelector('cart-items')?.syncCheckoutState(true);
  }

  restoreControls() {
    this.disabledControls.forEach((disabled, control) => { control.disabled = disabled; });
    this.disabledControls.clear();
  }

  errorText() {
    return this.querySelector('#CartDrawer')?.dataset.errorText || window.cartStrings.error;
  }

  setError(message) {
    const errors = this.querySelector('[data-gjl-drawer-error]') || this.querySelector('#CartDrawer-CartErrors');
    if (errors) errors.textContent = message;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = this.getSectionDOM(html, selector);
    if (!section) throw new Error(this.errorText());
    return section.innerHTML;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // The root survives section replacement and owns the subscription and locks.
  connectedCallback() {}

  onChange(event) {
    return this.closest('cart-drawer')?.validateQuantity(event);
  }

  onCartUpdate() {
    return this.closest('cart-drawer')?.refresh();
  }

  updateQuantity(line, quantity, event, name, variantId) {
    return this.closest('cart-drawer')?.changeQuantity(line, quantity, event, name, variantId);
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
